import { ComponentProps, forwardRef, useMemo } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { booleanishToBoolean, cn } from "~/lib/utils";

const textareaVariants = cva(
  "resize-none border-border placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base text-foreground transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
  {
    variants: {
      variant: {
        default: "",
        terminal: [
          "bg-gray-950 rounded-lg p-3 text-white/95 leading-[19px] font-[Courier_New]",
          "read-only:focus-visible:ring-0 read-only:focus-visible:border-border",
          "disabled:focus-visible:ring-0 disabled:focus-visible:border-border",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface TextareaProps extends ComponentProps<"textarea">, VariantProps<typeof textareaVariants> {
  /**
   * TODO: Add proper handling for formatted JSON for controlled components
   * Currently we only handle `defaultValue` since the only intended usa case for now is in read-only mode
   */
  formatJson?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      formatJson,
      defaultValue,
      className,
      variant,
      readOnly,
      "aria-readonly": ariaReadonlyBooleanish,
      tabIndex,
      ...props
    },
    ref
  ) => {
    const formattedDefaultValue = useMemo(
      () =>
        formatJson
          ? formatJsonString(defaultValue !== undefined ? defaultValue.toString() : "")
          : defaultValue,
      [defaultValue, formatJson]
    );

    const ariaReadonly = booleanishToBoolean(ariaReadonlyBooleanish ?? false);

    return (
      <textarea
        {...props}
        ref={ref}
        defaultValue={formattedDefaultValue}
        data-slot="textarea"
        spellCheck={variant === "terminal" ? "false" : undefined}
        readOnly={readOnly || ariaReadonly}
        aria-readonly={readOnly || ariaReadonly}
        className={cn(textareaVariants({ variant }), className)}
        tabIndex={readOnly || ariaReadonly || props.disabled ? -1 : tabIndex}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea };

function formatJsonString(value: string) {
  try {
    const jsonObject = JSON.parse(value);
    return JSON.stringify(jsonObject, null, 2);
  } catch (error) {
    console.error("Invalid JSON string:", error);
    return value; // Return the original value if it's not valid JSON
  }
}
